import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Drawer,
  Divider,
  Table,
  Popover,
  Tabs,
} from 'antd';
import PropTypes from 'prop-types';
import { FormHelpers } from 'ontraccr-common';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';

import WorkflowConfigureSourceSelector from './selectors/WorkflowConfigureSourceSelector';

import { getSourceFieldMap, getType, updateData } from './workflowHelpers';
import WorkflowSectionMappingConfigure from './WorkflowSectionMappingConfigure';

import { getTemplateDetails } from '../state/forms.actions';
import { createTargetToSourceMap, resolveFormDivisionIds } from '../formHelpers';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import HoverHelp from '../../common/HoverHelp';
import sortByString, { calculateStringSimilarity } from '../../helpers/helpers';
import {
  getResetMaterialsAddedDateColumn,
  isMaterialsTableField,
} from '../MaterialsMappingColumns';

const {
  getDuplicationEnabledSections,
  getFieldsDisabledBySectionMappings,
  resolveSectionMappings,
} = FormHelpers;

const { TabPane } = Tabs;

export default function WorkFlowTriggerConfigureDrawer({
  visible,
  onClose,
  onSubmit,
  targetFormId,
  sourceName,
  sourceSections = [],
  fieldMappings = {},
  onFieldMappingsChange,
  sectionMappings = {},
  onSectionMappingsChange,
  shouldAllowDuplicateSectionMapping = false,
  divisionId,
  divisionIds = [],
  templateId,
  parentLogicalLoopNode = null,
  zIndex = 1000,
  isFieldValid = () => true,
  canMapShiftToTime,
  fieldRenderMap = {},
  canResetMaterialsAddedDate = false,
  id,
  data = {},
  setDataMap,
}) {
  const dispatch = useDispatch();
  const templates = useSelector((state) => state.forms.templates);
  const customTables = useSelector((state) => state.forms.customTables);
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [selectedForm, setSelectedForm] = useState();

  const relevantDivisionIds = useMemo(
    () => resolveFormDivisionIds({ divisionIds, divisionId }),
    [divisionIds, divisionId],
  );

  const templateMatchesDivisions = useCallback((template) => {
    if (template.divisionIds?.length) {
      return template.divisionIds.some((id) => relevantDivisionIds.includes(id));
    }
    return relevantDivisionIds.includes(template.divisionId);
  }, [relevantDivisionIds]);

  const divTemplates = useMemo(() => {
    const templateArr = Object.values(templates)
      .filter((template) => template.id !== templateId
        && templateMatchesDivisions(template)
        && template.active
        && template.notDeleted);
    templateArr.sort(sortByString('name'));
    return templateArr;
  }, [
    templates,
    templateId,
    templateMatchesDivisions,
  ]);

  useEffect(() => {
    if (visible && !selectedForm && targetFormId?.startsWith('field-')) {
      setShowAllTemplates(true);
      setSelectedForm(divTemplates[0]?.id);
    }
  }, [visible, selectedForm, targetFormId, divTemplates]);

  useEffect(() => {
    if (visible && targetFormId && !targetFormId?.startsWith('field-')) {
      setShowAllTemplates(false);
      setSelectedForm(targetFormId);
    }
  }, [visible, targetFormId]);

  useEffect(() => {
    if (selectedForm && selectedForm !== templateId) {
      dispatch(getTemplateDetails(selectedForm));
    }
  }, [selectedForm, templateId]);

  const ourTemplate = useMemo(() => {
    const {
      [selectedForm]: targetForm = {},
    } = templates;

    const newForm = {
      ...targetForm,
    };

    if (!newForm.formData && newForm.jsonFormData) {
      try {
        newForm.formData = JSON.parse(newForm.jsonFormData);
      } catch (e) {
        // fail silently
      }
    }
    return newForm;
  }, [templates, selectedForm]);

  const {
    formData: {
      name: targetName,
      sections: targetSections = [],
    } = {},
  } = ourTemplate;

  const {
    flattenedTargetFields,
    formDropdownSubTypes,
  } = useMemo(() => {
    const fields = [];
    const subTypes = new Set();

    targetSections.forEach((section) => {
      const { fields: sectionFields = [], name: sectionName } = section;
      sectionFields.forEach((field) => {
        const {
          configProps: {
            title: fieldTitle,
            dataType,
            subDataType,
          } = {},
          selectedType,
        } = field;

        if (isFieldValid(field, customTables)) {
          if (selectedType === 'dropdown' && dataType === 'CompletedForms' && subDataType) {
            subTypes.add(subDataType);
          }

          fields.push({
            ...field,
            title: `${sectionName} - ${fieldTitle}`,
            fieldTitle,
          });
        }
      });
    });
    return {
      flattenedTargetFields: fields,
      formDropdownSubTypes: Array.from(subTypes),
    };
  }, [targetSections, isFieldValid, customTables]);

  const sourceFieldMap = useMemo(() => (
    getSourceFieldMap(sourceSections, parentLogicalLoopNode, formDropdownSubTypes)
  ), [sourceSections, parentLogicalLoopNode, formDropdownSubTypes]);

  const relevantSectionMappings = useMemo(
    () => resolveSectionMappings(sectionMappings, selectedForm),
    [sectionMappings, selectedForm],
  );

  const disabledSectionFieldIds = useMemo(() => {
    if (!shouldAllowDuplicateSectionMapping) return new Set();
    return getFieldsDisabledBySectionMappings(
      relevantSectionMappings,
      targetSections,
    );
  }, [
    shouldAllowDuplicateSectionMapping,
    relevantSectionMappings,
    targetSections,
  ]);

  const onSectionMappingsChangeInner = useCallback((nextMappings, formId) => {
    if (!onSectionMappingsChange) return;
    onSectionMappingsChange(nextMappings, formId || selectedForm);

    const nextDisabled = getFieldsDisabledBySectionMappings(nextMappings, targetSections);
    if (!nextDisabled.size) return;

    // Update the field mappings to remove the disabled fields
    const relevantFieldMappings = fieldMappings?.version === 'v2'
      ? createTargetToSourceMap(fieldMappings[selectedForm])
      : { ...fieldMappings };
    let changed = false;
    nextDisabled.forEach((fieldId) => {
      if (relevantFieldMappings[fieldId]) {
        delete relevantFieldMappings[fieldId];
        changed = true;
      }
    });
    if (changed) {
      onFieldMappingsChange(relevantFieldMappings, selectedForm);
    }
  }, [
    fieldMappings,
    onFieldMappingsChange,
    onSectionMappingsChange,
    selectedForm,
    targetSections,
  ]);

  const hasMaterialsTable = useMemo(
    () => flattenedTargetFields.some((field) => isMaterialsTableField(field)),
    [flattenedTargetFields],
  );

  const resetMaterialsAddedDate = useMemo(
    () => data.resetMaterialsAddedDate || {},
    [data],
  );

  const onResetMaterialsAddedDateChange = useCallback((recordId, checked) => {
    if (!setDataMap || !id) return;
    setDataMap(updateData(id, {
      resetMaterialsAddedDate: {
        ...resetMaterialsAddedDate,
        [recordId]: checked,
      },
    }));
  }, [resetMaterialsAddedDate, setDataMap, id]);

  const columns = useMemo(() => {
    const cols = [
      {
        dataIndex: 'sourceField',
        title: 'Source Field',
        width: 425,
        render: (_, record) => {
          const {
            id: targetId,
          } = record;
          const type = getType(record);
          const {
            [type]: eligibleSourceFields = [],
          } = sourceFieldMap;

          let allFields = eligibleSourceFields;

          if (type === 'table - TimeEntry' && canMapShiftToTime) {
            allFields = allFields.concat(
              sourceFieldMap['table - Shifts'] ?? [],
            );
          }

          const relevantFieldMappings = fieldMappings?.version === 'v2'
            ? createTargetToSourceMap(fieldMappings[selectedForm])
            : fieldMappings;

          const {
            [targetId]: defaultValue,
          } = relevantFieldMappings;

          if (disabledSectionFieldIds.has(targetId)) {
            return (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#8c8c8c' }}>
                Disabled
                <HoverHelp
                  content={(
                    <div style={{ maxWidth: 280 }}>
                      This field belongs to a duplicated section that is already mapped
                      in Duplicated Section Mapping. Map it there instead of the general
                      field mapping.
                    </div>
                  )}
                />
              </span>
            );
          }

          const eligibleSourceFieldIds = new Set(allFields.map(({ id: fieldId }) => fieldId));

          if (type in fieldRenderMap) {
            return fieldRenderMap[type]({
              fieldMappings: relevantFieldMappings,
              sourceFieldMap,
              defaultValue,
              onFieldMappingsChange,
              targetId,
              formId: selectedForm,
              value: defaultValue,
            });
          }

          // Remove the field mapping if the default value is no longer eligible
          if (defaultValue && !eligibleSourceFieldIds.has(defaultValue)) {
            const nextFieldMappings = { ...relevantFieldMappings };
            delete nextFieldMappings[targetId];
            onFieldMappingsChange(nextFieldMappings, selectedForm);
          }

          return (
            <WorkflowConfigureSourceSelector
              fieldMappings={relevantFieldMappings}
              defaultValue={defaultValue}
              onFieldMappingsChange={onFieldMappingsChange}
              targetId={targetId}
              eligibleSourceFields={allFields}
              formId={selectedForm}
              value={defaultValue}
            />
          );
        },
      }, {
        dataIndex: 'title',
        title: 'Target Field',
      },
    ];

    if (canResetMaterialsAddedDate && hasMaterialsTable) {
      cols.push(getResetMaterialsAddedDateColumn({
        valueMap: resetMaterialsAddedDate,
        onChange: onResetMaterialsAddedDateChange,
      }));
    }

    return cols;
  }, [
    sourceFieldMap,
    fieldMappings,
    selectedForm,
    onFieldMappingsChange,
    canMapShiftToTime,
    fieldRenderMap,
    canResetMaterialsAddedDate,
    hasMaterialsTable,
    resetMaterialsAddedDate,
    onResetMaterialsAddedDateChange,
    disabledSectionFieldIds,
  ]);

  const autoMap = useCallback(() => {
    const targetFieldMap = {};

    flattenedTargetFields.forEach((field) => {
      const {
        id: targetId,
        fieldTitle,
      } = field;

      const type = getType(field);
      const {
        [type]: eligibleSourceFields = [],
      } = sourceFieldMap;

      const relevantFieldMappings = fieldMappings?.version === 'v2'
        ? createTargetToSourceMap(fieldMappings[selectedForm])
        : fieldMappings;

      const {
        [targetId]: defaultValue,
      } = relevantFieldMappings;

      if (disabledSectionFieldIds.has(targetId)) return;

      // If a user has already selected a value, do not override
      if (defaultValue) {
        targetFieldMap[targetId] = {
          sourceId: defaultValue,
        };
        return;
      }

      eligibleSourceFields.forEach((sourceField) => {
        const {
          id: sourceId,
          fieldTitle: sourceTitle,
        } = sourceField;

        const stringSimilarity = calculateStringSimilarity(fieldTitle, sourceTitle);
        if (targetFieldMap[targetId]?.stringSimilarity > stringSimilarity) {
          return;
        }

        targetFieldMap[targetId] = {
          sourceId,
          stringSimilarity,
        };
      });
    });

    Object.keys(targetFieldMap).forEach((targetId) => {
      if (targetFieldMap[targetId].stringSimilarity < 0.3) {
        delete targetFieldMap[targetId];
        return;
      }

      targetFieldMap[targetId] = targetFieldMap[targetId].sourceId;
    });

    const newData = {
      ...fieldMappings,
      ...targetFieldMap,
    };

    onFieldMappingsChange(newData, selectedForm);
  }, [
    flattenedTargetFields,
    sourceFieldMap,
    selectedForm,
    fieldMappings,
    onFieldMappingsChange,
    disabledSectionFieldIds,
  ]);

  const hasSectionMapping = shouldAllowDuplicateSectionMapping
    && getDuplicationEnabledSections(sourceSections).length > 0
    && getDuplicationEnabledSections(targetSections).length > 0;
  const tableScroll = hasSectionMapping ? undefined : { y: 'calc(100vh - 218px)' };

  return (
    <Drawer
      title="Configure Field Mapping"
      width={1000}
      onClose={onClose}
      visible={visible}
      zIndex={zIndex}
      bodyStyle={hasSectionMapping ? { paddingBottom: 60 } : undefined}
    >
      Configure which fields from this <b>{sourceName}</b> form are loaded into the fields for the <b>{showAllTemplates ? 'selected' : targetName}</b> form
      <Popover
        placement="left"
        content={(
          <div style={{ maxWidth: 200 }}>
            Auto map will attempt to map fields with similar names or type for the selected form.
            If no fields are mapped, you will need to manually map the fields.
          </div>
        )}
      >
        <OnTraccrButton
          title="Auto Map"
          onClick={autoMap}
          style={{ float: 'right' }}
        />
      </Popover>
      <Divider style={{ marginBottom: shouldAllowDuplicateSectionMapping ? 16 : 0 }} />
      {shouldAllowDuplicateSectionMapping && (
        <WorkflowSectionMappingConfigure
          sourceSections={sourceSections}
          targetSections={targetSections}
          sectionMappings={relevantSectionMappings}
          onSectionMappingsChange={onSectionMappingsChangeInner}
          formId={selectedForm}
          isFieldValid={(field) => isFieldValid(field, customTables)}
        />
      )}
      { showAllTemplates && (
        <Tabs
          tabPosition="left"
          activeTab={selectedForm}
          onChange={setSelectedForm}
          destroyInactiveTabPane
          style={hasSectionMapping ? undefined : { height: '100%' }}
        >
          {divTemplates.map((template) => (
            <TabPane
              tab={(
                <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {template.name}
                </div>
              )}
              key={template.id}
            >
              <Table
                dataSource={flattenedTargetFields}
                columns={columns}
                pagination={false}
                size="small"
                scroll={tableScroll}
                rowKey="id"
              />
            </TabPane>
          ))}
        </Tabs>
      )}
      { !showAllTemplates && (
        <Table
          dataSource={flattenedTargetFields}
          columns={columns}
          pagination={false}
          size="small"
          scroll={tableScroll}
          rowKey="id"
        />
      )}
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </Drawer>
  );
};

WorkFlowTriggerConfigureDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  targetFormId: PropTypes.string.isRequired,
  sourceName: PropTypes.string.isRequired,
  sourceSections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      configProps: PropTypes.shape({
        title: PropTypes.string.isRequired,
      }).isRequired,
    })).isRequired,
  })).isRequired,
  fieldMappings: PropTypes.shape({
    version: PropTypes.string,
  }).isRequired,
  onFieldMappingsChange: PropTypes.func.isRequired,
  sectionMappings: PropTypes.shape({}),
  onSectionMappingsChange: PropTypes.func,
  shouldAllowDuplicateSectionMapping: PropTypes.bool,
  divisionId: PropTypes.string.isRequired,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  templateId: PropTypes.string.isRequired,
  parentLogicalLoopNode: PropTypes.string,
  zIndex: PropTypes.number,
  isFieldValid: PropTypes.func,
  canMapShiftToTime: PropTypes.bool,
  fieldRenderMap: PropTypes.shape({}),
  canResetMaterialsAddedDate: PropTypes.bool,
  id: PropTypes.string,
  data: PropTypes.shape({
    resetMaterialsAddedDate: PropTypes.shape({}),
  }),
  setDataMap: PropTypes.func,
};

WorkFlowTriggerConfigureDrawer.defaultProps = {
  divisionIds: [],
  parentLogicalLoopNode: null,
  zIndex: 1000,
  isFieldValid: () => true,
  canMapShiftToTime: false,
  fieldRenderMap: {},
  canResetMaterialsAddedDate: false,
  id: undefined,
  data: {},
  setDataMap: undefined,
  sectionMappings: {},
  onSectionMappingsChange: undefined,
  shouldAllowDuplicateSectionMapping: false,
};
